import { Fragment, memo } from "react";
import { TestingUserBubble } from "@/components/molecules/testing/TestingUserBubble";
import { TurnTimestamp } from "@/components/molecules/testing/TurnTimestamp";
import { ThinkingPanel } from "@/components/molecules/testing/ThinkingPanel";
import type { RenderedMessage, TestingChatActivity } from "@/stores/testingStore";
import type { ChatTurn } from "./chatTimeline";
import { AssistantMessage } from "./AssistantMessage";
import { PlanProposalCard, type PlanProposalCardProps } from "./PlanProposalCard";

/**
 * Everything the parent needs to supply for ONE proposed plan inside a turn. Resolved per message by
 * the panel (approval state is per-plan and depends on the live plan list, not on the snapshot the
 * message carries), then handed down as data.
 */
export type PlanBindings = Omit<PlanProposalCardProps, "plan" | "planContract" | "intentContract" | "intentGaps" | "planDiff">;

export interface TestingChatTurnProps {
	turn: ChatTurn;
	/** Per-message plan controls, keyed by message id. */
	planBindingsFor: (message: RenderedMessage) => PlanBindings | null;
	/**
	 * Run result cards + heal proposal anchored under a given assistant message. The flat index is
	 * passed explicitly because run anchors are keyed by position in the whole message list — looking
	 * it up with `indexOf` would be a linear scan per plan card on every render.
	 */
	renderAnchoredResults: (message: RenderedMessage, flatIndex: number) => React.ReactNode;
	/** True only for the last turn while the planner is streaming into it. */
	streaming: boolean;
	/** Live planner phase, used by the in-flight turn's thinking panel. */
	phase: string | null;
	/** Live activity for the in-flight turn; finished turns use their own snapshot. */
	liveActivity: TestingChatActivity[];
}

/**
 * One question-and-answer exchange.
 *
 * The thread used to be a flat `messages.map` emitting cards 6px apart with no grouping, so a
 * session read as an undifferentiated stack of boxes — you could not see where your question ended
 * and the agent's work began. A turn now owns its block: the question, then the work, then the
 * answer and its artefacts, closed by a single hairline.
 *
 * Memoized because the panel re-renders on every streamed activity event, and a turn can contain a
 * plan preview table and a Monaco spec.
 */
export const TestingChatTurn = memo(function TestingChatTurn({
	turn,
	planBindingsFor,
	renderAnchoredResults,
	streaming,
	phase,
	liveActivity,
}: TestingChatTurnProps) {
	return (
		<article className="tst-turn tst-measure tst-fade-up">
			{turn.user && (
				<div className="mb-3 flex flex-col items-end gap-1">
					<TestingUserBubble content={turn.user.content} />
					<TurnTimestamp at={turn.user.at ?? turn.assistant[0]?.at} />
				</div>
			)}

			<div className="flex flex-col gap-3">
				{turn.assistant.map((message, assistantIndex) => {
					const bindings = message.proposedPlan ? planBindingsFor(message) : null;
					// A turn's messages are contiguous, so the flat position is derivable without a scan.
					const flatIndex = turn.startIndex + (turn.user ? 1 : 0) + assistantIndex;
					// Only the LAST message of a live turn owns the running stream; earlier ones show the
					// trail they captured, so a turn never renders two "Working" panels at once.
					const isLive = streaming && assistantIndex === turn.assistant.length - 1;
					const activity = isLive ? liveActivity : (message.chatActivity ?? []);
					return (
						<Fragment key={message.id}>
							{activity.length > 0 || isLive ? (
								<ThinkingPanel
									streaming={isLive}
									phase={isLive ? phase : null}
									activity={activity}
								/>
							) : null}

							<AssistantMessage message={message} />

							{message.proposedPlan && bindings && (
								// data-plan-card anchors the left "plans in this session" list to the card: selecting a
								// plan scrolls its card into view (scroll-mt keeps it clear of the turn's top edge).
								<div data-plan-card={message.proposedPlan.id} className="scroll-mt-3">
									<PlanProposalCard
										plan={message.proposedPlan}
										planContract={message.planContract}
										intentContract={message.intentContract}
										intentGaps={message.intentGaps}
										planDiff={message.metadata?.planDiff as Record<string, unknown> | undefined}
										{...bindings}
									/>
								</div>
							)}

							{renderAnchoredResults(message, flatIndex)}
						</Fragment>
					);
				})}

				{/* A turn that has been sent but has no assistant message yet still needs to look alive. */}
				{streaming && turn.assistant.length === 0 && (
					<ThinkingPanel streaming phase={phase} activity={liveActivity} />
				)}
			</div>
		</article>
	);
});
